import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/Tabs";
import ResearchCard from "./ResearchCard";
import { ChevronRight } from "lucide-react";

// Container animation to stagger the cards
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.2,
    },
  },
};

// Individual card animation
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const ResearchTabsSection = () => {
  const [activeTab, setActiveTab] = useState("models");

  return (
    <section className="py-20 bg-white dark:bg-gray-800">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <Tabs
          defaultValue="models"
          value={activeTab}
          onValueChange={(value) => setActiveTab(value)}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2 mb-12">
            <TabsTrigger value="models">Open-Source Models</TabsTrigger>
            <TabsTrigger value="research">Current Research</TabsTrigger>
          </TabsList>

          <TabsContent value="models">
            <motion.div
              key="models"
              className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 items-stretch"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {[
                {
                  title: "GPT-Neo",
                  description:
                    "A powerful language model for text generation and understanding.",
                  tags: ["NLP", "Text Generation", "Sentiment Analysis"],
                  link: "https://example.com/gpt-neo",
                },
                {
                  title: "BERT",
                  description:
                    "Bidirectional Encoder Representations from Transformers for NLP tasks.",
                  tags: [
                    "NLP",
                    "Text Classification",
                    "Named Entity Recognition",
                  ],
                  link: "https://example.com/bert",
                },
                {
                  title: "Stable Diffusion",
                  description:
                    "State-of-the-art text-to-image generation model.",
                  tags: [
                    "Computer Vision",
                    "Image Generation",
                    "Text-to-Image",
                  ],
                  link: "https://example.com/stable-diffusion",
                },
              ].map((item, index) => (
                <motion.a
                  key={index}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-full block bg-white border-2 border-transparent hover:shadow-lg hover:border-red-400 rounded-xl"
                  variants={cardVariants}
                >
                  <ResearchCard
                    title={item.title}
                    description={item.description}
                    tags={item.tags}
                  />
                </motion.a>
              ))}
            </motion.div>
          </TabsContent>

          <TabsContent value="research">
            <motion.div
              key="research"
              className="grid gap-8 md:grid-cols-2 items-stretch"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {[
                {
                  title: "Quantum-Inspired AI Algorithms",
                  description:
                    "Harnessing quantum computing principles to revolutionize AI processing and problem-solving capabilities.",
                  tags: [
                    "Quantum Computing",
                    "Algorithm Design",
                    "Optimization",
                  ],
                  link: "https://example.com/quantum-ai",
                },
                {
                  title: "Neuro-Symbolic AI",
                  description:
                    "Bridging neural networks and symbolic reasoning for more robust and explainable AI systems.",
                  tags: ["Neural Networks", "Symbolic AI", "Explainable AI"],
                  link: "https://example.com/neuro-symbolic-ai",
                },
              ].map((item, index) => (
                <motion.a
                  key={index}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-full block bg-white  border-2 border-transparent wrounded-lg"
                  variants={cardVariants}
                >
                  <ResearchCard
                    title={item.title}
                    description={item.description}
                    tags={item.tags}
                  />
                </motion.a>
              ))}
            </motion.div>
          </TabsContent>
        </Tabs>

        <div className="mt-12 text-center">
          <a
            href="#"
            className="inline-flex items-center text-gray-900 dark:text-gray-100 hover:underline"
          >
            View all {activeTab === "models" ? "models" : "research projects"}
            <ChevronRight className="ml-1 h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ResearchTabsSection;
